import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { EditorBlock } from '@/store/editorSlice';

/**
 * Гибкий отступ — аналог SwiftUI `Spacer()`: блок «Отступ» в режиме
 * «Заполнить свободное место» (`spacerFlexible`) забирает остаток высоты
 * экрана и прижимает нижние блоки к низу. Нет свободного места — остаётся
 * своей минимальной высоты (`heightPx`), скролла не добавляет. Фиксированный
 * отступ так не умеет: на узком телефоне он просто удлиняет экран.
 */
export function isFlexibleSpacer(block: EditorBlock): boolean {
  return block.type === 'spacer' && block.spacerFlexible === true;
}

export function screenHasFlexibleSpacer(
  blocks: readonly EditorBlock[] | undefined,
): boolean {
  return Array.isArray(blocks) && blocks.some(isFlexibleSpacer);
}

/**
 * Растёт на свободное место (поровну с другими гибкими отступами экрана),
 * не сжимается ниже содержимого — то есть ниже минимальной высоты, которую
 * рисует сам блок. Внутри — колонка: единственный ребёнок (в редакторе это
 * кликабельная зона блока) растягивается на всю высоту правилом global.css
 * `.screen-flex-spacer > *`.
 */
export const FLEX_SPACER_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: 'auto',
};

/**
 * Раскладывает узлы колонки экрана вокруг гибких отступов:
 * [блоки до] [отступ] [блоки после] …
 *
 * ПОЧЕМУ ГРУППЫ, А НЕ FLEX НА ВСЮ КОЛОНКУ. Во flex-контейнере вертикальные
 * поля соседей не схлопываются — интервалы между ВСЕМИ блоками экрана
 * разъехались бы (правило соседства blockSpacing.tsx держится на схлопывании).
 * Группа — обычный блок, и внутри неё всё как раньше; flex-элементы колонки —
 * только группы и сами отступы.
 *
 * ПОЧЕМУ ОБЁРТКА СНАРУЖИ. Вокруг div отступа бывает до четырёх условных
 * обёрток (фон, поля, внутренняя поверхность, кликабельная зона редактора) —
 * flex на внутреннем div до колонки не дотянулся бы. Визард знает тип блока и
 * оборачивает узел целиком.
 *
 * `nodes[i]` — отрисованный `blocks[i]`, `null` — блок скрыт. Экран без
 * гибкого отступа получает свои узлы обратно БЕЗ изменений: разметка
 * выкаченных воронок не меняется.
 *
 * Отступ есть, но скрыт правилом (ревью PR #28, находка 1) — группы всё равно
 * ставим: признак `fill` считается по составу экрана, колонка уже flex, и
 * россыпь блоков в ней потеряла бы схлопывание полей. Заодно разметка не
 * прыгает между «россыпью» и «группами», когда отступ показывается и прячется.
 *
 * Ключ группы — предыдущий гибкий отступ, а не первый блок группы (находка 2):
 * первый блок может спрятаться правилом видимости, и ключ от него пересоздал
 * бы всю группу вместе с полями ввода, потерявшими фокус.
 *
 * Сознательно НЕ в группе: хвост колонки, который визард ставит после блоков
 * (запасная «Далее», повтор кнопки покупки). Он бывает липким (`sticky`), а
 * липкий внутри группы прилипал бы лишь в её границах. Цена — поле между
 * последним блоком и этим хвостом на экране с гибким отступом складывается,
 * а не схлопывается (ревью PR #28, находка 3, принято осознанно).
 */
export function arrangeFlexSpacers(
  blocks: readonly EditorBlock[],
  nodes: readonly ReactNode[],
): ReactNode {
  if (!blocks.some(isFlexibleSpacer)) return nodes;

  const out: ReactNode[] = [];
  let run: ReactNode[] = [];
  let groupKey = 'head';
  const flush = (): void => {
    if (run.length === 0) return;
    out.push(
      <div key={`group-${groupKey}`} data-flex-spacer-group="">
        {run}
      </div>,
    );
    run = [];
  };

  blocks.forEach((block, i) => {
    const node = nodes[i];
    if (node == null) return;
    if (isFlexibleSpacer(block)) {
      flush();
      groupKey = `after-${block.id}`;
      out.push(
        <div
          key={`flex-spacer-${block.id}`}
          className="screen-flex-spacer"
          data-testid="screen-flex-spacer"
          style={FLEX_SPACER_STYLE}
        >
          {node}
        </div>,
      );
      return;
    }
    run.push(node);
  });
  flush();
  return out;
}
